import Dom from '../../helpers/dom-helper';
import Storage from '../../helpers/storage';
import modalTemplate from './consent-modal.html';

const MODAL_TEMPLATE = modalTemplate();
const BODY_CONSENT_MODAL_CLASSNAME = 'consent-modal-open';
const STORAGE_KEY_CONSENT_HIDDEN = 'privacy-modal-hidden';

const ONE_WEEK_IN_MS = 604800000;

const CONSENT_TYPE_OPTIONAL = 'optional';
const CONSENT_TYPE_MANDATORY = 'mandatory';

export default class ConsentModal {
    constructor(container, domManager, eventBus, consentType, privacyPolicyUrl, onConsentAccepted, onViewContent) {
        this.consentType = consentType;
        this.privacyPolicyUrl = privacyPolicyUrl;
        this.container = container;
        this.domManager = domManager;
        this.eventBus = eventBus;
        this.element = null;
        this.onSubmitButtonClick = this.onSubmitButtonClick.bind(this);
        this.onCheckboxChange = this.onCheckboxChange.bind(this);
        this.onViewContentButtonClick = this.onViewContentButtonClick.bind(this);
        this.onViewContent = onViewContent;
        this.onConsentAccepted = onConsentAccepted;
        this.isOpen = false;

        this.render();
    }

    open() {
        this.isOpen = true;
        Dom.addClass(BODY_CONSENT_MODAL_CLASSNAME, this.domManager.get('body'));
    }

    close() {
        this.isOpen = false;
        Dom.removeClass(BODY_CONSENT_MODAL_CLASSNAME, this.domManager.get('body'));
        this.destroy();
        this.eventBus.dispatch('modal:close');
    }

    /**
     * Render the modal in the DOM
     */
    render() {
        const translations = window.__SP__translations['privacy-policy'];
        const { welcome, confirmation, continue: continueBtn, viewContent } = translations;

        this.element = Dom.createElement(MODAL_TEMPLATE);
        this.element.querySelector('.modal-consent__header').textContent = welcome;
        this.element.querySelector('.modal-consent__accept .control__label--custom').innerHTML = confirmation;
        this.element.querySelector('.modal-consent__continue span').textContent = continueBtn;

        if (this.consentType === CONSENT_TYPE_OPTIONAL) {
            this.element.querySelector(
                '.modal-consent__message'
            ).innerHTML = `<p>${translations.message}</p><p>${translations.messageOptional}</p>`;
            const viewContentButton = Dom.createElement(
                `<button class="button--m consent-modal-view-content-button button button-tint--reversed">${viewContent}</button>`
            );
            this.element.querySelector('.modal-consent__buttons').appendChild(viewContentButton);
        } else {
            this.element.querySelector('.modal-consent__message').innerHTML = `<p>${translations.message}</p>`;
        }

        this.element.querySelector('.privacy-policy-link').href = this.privacyPolicyUrl;
        this.element.querySelector('.message__privacy-policy-link').href = this.privacyPolicyUrl;
        this.container.appendChild(this.element);
        this.domManager.set('consent-modal-button', this.element.querySelector('.consent-modal-button'));
        if (this.consentType === CONSENT_TYPE_OPTIONAL) {
            this.domManager.set('consent-modal-view-content-button', this.element.querySelector('.consent-modal-view-content-button'));
        }
        this.domManager.set('consent-modal-checkbox', this.element.querySelector('.consent-modal-checkbox'));

        this.addEventListeners();
    }

    destroy() {
        this.removeEventListeners();
    }

    /**
     * Add event listeners for this modal
     */
    addEventListeners() {
        Dom.on(this.domManager.get('consent-modal-button'), 'click', this.onSubmitButtonClick);
        if (this.consentType === CONSENT_TYPE_OPTIONAL) {
            Dom.on(this.domManager.get('consent-modal-view-content-button'), 'click', this.onViewContentButtonClick);
        }
        Dom.on(this.domManager.get('consent-modal-checkbox'), 'change', this.onCheckboxChange);
    }

    removeEventListeners() {
        Dom.off(this.domManager.get('consent-modal-button'), 'click', this.onSubmitButtonClick);
        if (this.consentType === CONSENT_TYPE_OPTIONAL) {
            Dom.off(this.domManager.get('consent-modal-view-content-button'), 'click', this.onViewContentButtonClick);
        }
        Dom.off(this.domManager.get('consent-modal-checkbox'), 'change', this.onCheckboxChange);
    }

    onCheckboxChange(event) {
        if (event.target.checked) {
            this.domManager.get('consent-modal-button').removeAttribute('disabled');
            if (this.consentType === CONSENT_TYPE_OPTIONAL) {
                this.domManager.get('consent-modal-view-content-button').style.display = 'none';
            }
        } else {
            this.domManager.get('consent-modal-button').setAttribute('disabled', true);
            if (this.consentType === CONSENT_TYPE_OPTIONAL) {
                this.domManager.get('consent-modal-view-content-button').style.display = 'block';
            }
        }
    }

    onSubmitButtonClick() {
        this.onConsentAccepted();
        this.close();
    }

    onViewContentButtonClick() {
        // In case the user proceeds without giving consent, (Optional Consent),
        // store the timestamp and wait a week to show the modal again
        Storage.set(STORAGE_KEY_CONSENT_HIDDEN, Date.now());
        this.onViewContent();
        this.close();
    }

    static shouldShow(consentRequired, consentType, consentEnabled) {
        if (consentEnabled === false || consentRequired === false) {
            return Promise.resolve(false);
        }
        if (consentType === CONSENT_TYPE_MANDATORY) {
            return Promise.resolve(true);
        }
        if (consentType === CONSENT_TYPE_OPTIONAL) {
            return ConsentModal.consentHidden().then(consentHidden => !consentHidden);
        }

        return Promise.resolve(false);
    }

    static consentHidden() {
        return Storage.get(STORAGE_KEY_CONSENT_HIDDEN)
            .catch(() => 0)
            .then(value => {
                const timestamp = parseInt(value, 10);
                const showPolicy = !(Date.now() - timestamp < ONE_WEEK_IN_MS);

                return !showPolicy;
            });
    }
}
